import { getScrollParent } from '@react-aria/utils';
import { useVirtualizer, type VirtualItem, type Virtualizer } from '@tanstack/react-virtual';
import * as React from 'react';

import { useLiveRef } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

export type MenuVirtualRendererItem = { id: string };

export type MenuVirtualRendererRef = {
    scrollToId: (id: string) => void;
};

export type MenuVirtualRendererProps<T extends MenuVirtualRendererItem> = {
    items: T[];
    onMeasureWidth: (width: number) => void;
    estimateSize: (index: number) => { width: number; height: number };
    renderItem: (
        virtualItem: VirtualItem,
        item: T,
        virtualizer: Virtualizer<Element, Element>
    ) => React.ReactNode;
};

export const MenuVirtualRenderer = React.forwardRef(function MenuVirtualRenderer<
    T extends MenuVirtualRendererItem,
>(props: MenuVirtualRendererProps<T>, ref: React.ForwardedRef<MenuVirtualRendererRef>) {
    const { items, renderItem, estimateSize, onMeasureWidth } = props;
    const contentRef = React.useRef<HTMLDivElement>(null);
    const refs = useLiveRef({ onMeasureWidth, estimateSize });
    React.useLayoutEffect(() => {
        const { onMeasureWidth, estimateSize } = refs.current;
        for (let i = 0; i < items.length; i++) {
            const { width } = estimateSize(i);
            onMeasureWidth(width);
        }
    }, [items, refs]);
    const virtualizer = useVirtualizer({
        count: items.length,
        getScrollElement: () => (contentRef.current ? getScrollParent(contentRef.current) : null),
        estimateSize: (index) => estimateSize(index).height,
        overscan: 5,
        paddingStart: 4,
        paddingEnd: 4,
        getItemKey: (index) => {
            assert(items[index]);
            return items[index].id;
        },
        measureElement: (element) => {
            const rect = element.getBoundingClientRect();
            const width = rect.width;
            onMeasureWidth(width);
            return rect.height;
        },
    });
    const { scrollToIndex } = virtualizer;
    const itemsRef = useLiveRef(items);
    React.useImperativeHandle(
        ref,
        () => ({
            scrollToId: (id) => {
                const items = itemsRef.current;
                const index = items.findIndex((i) => i.id === id);
                if (index !== -1) {
                    scrollToIndex(index);
                }
            },
        }),
        [scrollToIndex, itemsRef]
    );
    const virtualItems = virtualizer.getVirtualItems();
    return (
        <div
            ref={contentRef}
            className="relative"
            style={{
                height: virtualizer.getTotalSize(),
            }}
        >
            <div
                className="flex min-w-full flex-col"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
                }}
            >
                {virtualItems.map((virtualItem) => {
                    const item = items[virtualItem.index];
                    if (!item) {
                        return null;
                    }
                    return renderItem(virtualItem, item, virtualizer);
                })}
            </div>
        </div>
    );
}) as <T extends MenuVirtualRendererItem>(
    props: MenuVirtualRendererProps<T> & { ref?: React.ForwardedRef<MenuVirtualRendererRef> }
) => React.ReactElement;
